import React, { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import "./Login.css";
import { encrypt, decrypt } from "../../Encrypdecryp/Crypt.js";

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const formData = new FormData();
    const encryptedEmail = encrypt(email);
    const encryptedPassword = encrypt(password);

    console.log("Encrypted Email:", encryptedEmail);
    console.log("Encrypted Password:", encryptedPassword);

    if (!encryptedEmail || !encryptedPassword) {
      const errorMsg = "Failed to encrypt credentials.";
      setError(errorMsg);
      setIsLoading(false);
      toast.error(errorMsg);
      return;
    }

    formData.append("username", encryptedEmail);
    formData.append("password", encryptedPassword);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      if (!API_BASE_URL) {
        throw new Error('API_BASE_URL is not defined in .env');
      }
      const login_Url = `${API_BASE_URL}/login/access-token`;

      try {
        new URL(login_Url);
      } catch (urlError) {
        throw new Error(`Invalid API URL: ${login_Url}`);
      }

      const response = await axios.post(login_Url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const token = response.data.access_token;
      const encryptedRole = response.data.role || "";
      const role = decrypt(encryptedRole);
      const requiresPasswordChange = response.data.requires_password_change || false;

      console.log("Login Response:", response.data);
      console.log("Requires Password Change:", requiresPasswordChange);

      if (!token || !role) {
        throw new Error("Invalid response: Missing token or role.");
      }

      // Store auth data
      localStorage.setItem("authToken", token);
      localStorage.setItem("userRole", role);
      localStorage.setItem("username", email);

      if (requiresPasswordChange === true) {
        console.log("🔐 Password change required - redirecting to /change-password");
        
        toast.info("Password change required. Please create a new password to continue.", {
          autoClose: 3000,
        });

        setTimeout(() => {
          navigate("/change-password", { state: { token } });
        }, 1000);
        
        setIsLoading(false);
        return; 
      }

      toast.success("Welcome back! Login successful.", {
        autoClose: 2000,
      });

      if (onLoginSuccess) {
        onLoginSuccess();
      }

      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (err) {
      console.error("Login error:", err);
      console.error("Response data:", err.response?.data);
      
      let errorMessage = "Login failed. Please try again.";
      if (err.response) {
        if (err.response.status === 500) {
          errorMessage = "Server error. Please try again later or contact support.";
        } else if (err.response.status === 401) {
          errorMessage = "Invalid credentials. Please check your username or password.";
        } else if (err.response.status === 0) {
          errorMessage = "CORS error: Unable to reach the server. Please check server configuration.";
        }
      } else if (err.message.includes("Network Error")) {
        errorMessage = "Network error: Unable to connect to the server. Check your connection or server status.";
      } else if (err.message.includes("Invalid API URL")) {
        errorMessage = "Invalid API URL configuration. Please contact support.";
      }
      
      setError(errorMessage);
      toast.error(`Login failed: ${errorMessage}`, {
        autoClose: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="login-container" maxWidth={false}>
      <Box className="login-grid">
        {/* Left Side - Branding */}
        <Box className="login-brand-section">
          <Box className="brand-content">
            <Typography variant="h2" className="brand-title">
              boTeye TradeDocs
            </Typography>
            <Typography variant="body1" className="brand-subtitle">
              AI-Powered Trade Finance Document Management
            </Typography>
            <Box className="brand-features">
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">AI-powered document extraction & processing</Typography>
              </Box>
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">Import & Export workflows</Typography>
              </Box>
              <Box className="feature-item">
                <Box className="feature-icon">✓</Box>
                <Typography className="feature-text">Advance document search</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Side - Login Form */}
        <Box className="login-form-section">
          <Paper className="login-paper" elevation={0}>
            <Box className="form-header">
              <Typography variant="h4" className="form-title">
                Welcome Back
              </Typography>
              <Typography variant="body2" className="form-subtitle">
                Please enter your credentials to continue
              </Typography>
            </Box>

            {error && (
              <Box className="error-message">
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            <form onSubmit={handleLogin}>
              <Box className="input-group">
                <TextField
                  label="Email Address"
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  required
                  type="email"
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email className="input-icon" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box className="input-group">
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field"
                  required
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock className="input-icon" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box className="forgot-password">
                <Link to="/forgot-password">Forgot Password?</Link>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Signing In...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <Divider className="divider">
              <Typography variant="body2" className="divider-text">
                OR
              </Typography>
            </Divider>

            <Typography className="footer-text">
              Don't have an account?{" "}
              <Link to="/signup" className="signup-link">
                Create Account
              </Link>
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;